/**
 *  @organization: Nanocosmos GmbH
 *
 *  Legend and axis helpers shared by the alert charts.
 *
 *  The charts draw their metric thresholds as annotations, which chart.js knows nothing about: they get no
 *  legend entry, and labelling them on the plot makes them cover each other as soon as two thresholds sit
 *  close together. So the lines are described in the legend instead, and what each one actually decides -
 *  which needs knowledge of the detection - goes into the info tooltip of the widget header, on demand.
 */

import React from 'react';
import { PropTypes } from 'prop-types';
import Chart from 'chart.js';

// Zero-length datasets: they draw nothing themselves but give each threshold a legend entry in the colour of
// its line. Append them after the real datasets, so realDatasetCount below stays a simple boundary.
export function getThresholdLegendDatasets(thresholds) {
   return thresholds.map(({ label, color, borderDash }) => ({
      label,
      borderColor: color,
      backgroundColor: color,
      borderDash,
      borderWidth: 2,
      pointRadius: 0,
      data: [],
      fill: false,
      showLine: false,
   }));
}

// Toggling a threshold entry would strike its label through without hiding anything, since the line it stands
// for is an annotation rather than a dataset. The real datasets keep the default toggle behaviour.
function makeLegendClickHandler(realDatasetCount) {
   return function handleLegendClick(event, legendItem) {
      if (legendItem.datasetIndex >= realDatasetCount) return;
      const defaultOnClick = Chart?.defaults?.global?.legend?.onClick;
      if (typeof defaultOnClick === 'function') defaultOnClick.call(this, event, legendItem);
   };
}

// The shared troubleshooting options draw the legend key 1px wide, which is enough for a colour dot but not
// for a threshold entry: its colour and its dash pattern are the only thing that ties it to the line on the
// plot it describes, and at 1px all of them look alike.
const LEGEND_BOX_WIDTH = 30;

// The shared chart option factories have no notion of units or of legend-only datasets, so both are applied
// here: the y axis gets a caption, the tooltip names the dataset it belongs to, the legend key gets wide
// enough to read, and the threshold entries become inert.
export function withAxisLabelAndLegendHandling(options, { axisLabel, formatValue, realDatasetCount }) {
   options.scales.yAxes[0].scaleLabel = { display: true, labelString: axisLabel };
   options.tooltips.callbacks.label = (tooltipItem, data) =>
      `${data.datasets[tooltipItem.datasetIndex].label}: ${formatValue(tooltipItem.yLabel)}`;
   options.legend.labels = { ...options.legend.labels, boxWidth: LEGEND_BOX_WIDTH };
   options.legend.onClick = makeLegendClickHandler(realDatasetCount);
   return options;
}

// What the legend cannot carry without turning into a paragraph: which line decides what.
export function ChartExplanation({ alertInfo, chartNote, thresholds }) {
   return (
      <div style={{ textAlign: 'left', maxWidth: '380px' }}>
         <div>{alertInfo}</div>
         <div style={{ marginTop: '8px', fontWeight: 600 }}>What the chart lines mean</div>
         {chartNote && <div style={{ marginTop: '2px' }}>{chartNote}</div>}
         <ul style={{ margin: '6px 0 0 0', paddingLeft: '16px' }}>
            {thresholds.map(({ label, explanation }) => (
               <li key={label} style={{ marginTop: '4px' }}>
                  <span style={{ fontWeight: 600 }}>{label}</span> — {explanation}
               </li>
            ))}
         </ul>
      </div>
   );
}

const thresholdShape = PropTypes.arrayOf(
   PropTypes.shape({
      label: PropTypes.string,
      color: PropTypes.string,
      borderDash: PropTypes.arrayOf(PropTypes.number),
      explanation: PropTypes.string,
   }),
);

ChartExplanation.propTypes = {
   alertInfo: PropTypes.string,
   chartNote: PropTypes.string,
   thresholds: thresholdShape,
};
